import { upgradeReferralClick } from '@wix/bi-logger-pricing-plans-data/v2';
import { TPA_EXPERIMENTS } from '@wix/pricing-plans-common/experiments';
import { ControllerFlowAPI } from '@wix/yoshi-flow-editor';
import { AppProps, ToastData } from '../../../types/common';
import { toError } from '../../../utils/errors';
import { CheckoutController } from './CheckoutController';
import { ListController } from './ListController';
import { RestrictedController } from './RestrictedController';
import { Router } from './Router';
import { StatusController } from './StatusController';

export class App {
  constructor(
    public setProps: (props: Partial<AppProps>) => void,
    protected list: ListController,
    protected checkout: CheckoutController,
    protected status: StatusController,
    protected restricted: RestrictedController,
    protected router: Router,
    protected flowAPI: ControllerFlowAPI,
  ) {}

  public async initialize() {
    this.setProps({
      hideToast: this.hideToast,
      showToast: this.showToast,
      biUpgradeReferralClick: (referralInfo: string) =>
        this.flowAPI.bi?.report(upgradeReferralClick({ referralInfo, isPreview: false })),
    });

    this.router.whenInit({
      list: (integrationData) => this.list.initialize(integrationData),
      checkout: (checkoutData) => this.checkout.initialize(checkoutData),
      status: (statusData) => this.status.initialize(statusData),
      restricted: ({ restrictedPageParams, integrationData }) =>
        this.restricted.initialize(restrictedPageParams, integrationData),
    });

    this.router.whenNavigate({
      list: ({ integrationData, toast }) => {
        this.setProps({ toast });
        return this.list.update(integrationData);
      },
      checkout: async ({ integrationData, plan, order, checkoutFlowId }) => {
        this.setProps({ selectedPlan: plan });
        return this.checkout.update({
          selectedPlan: plan,
          integrationData,
          order,
          checkoutFlowId,
        });
      },
      status: ({ statusData, plan }) => this.status.update(statusData, plan),
      restricted: ({ restrictedPageParams, integrationData }) =>
        this.restricted.update(restrictedPageParams, integrationData),
    });

    try {
      await this.router.initialize();
    } catch (e) {
      this.flowAPI.reportError(toError(e));
      const { message } = this.flowAPI.errorHandler.getResolvedError(e);
      const isErrorHandlerEnabled = this.flowAPI.experiments.enabled(TPA_EXPERIMENTS.ERROR_HANDLER_TPA);
      await this.list.initialize({});
      this.setProps({
        toast: {
          message: isErrorHandlerEnabled ? message : this.flowAPI.translations.t('pp.generic-purchase-error'),
        },
      });
    }
  }

  hideToast = () => this.setProps({ toast: undefined });
  showToast = (toast: ToastData) => this.setProps({ toast });
}
